import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Wrench, CalendarClock, UserX, AlertTriangle, Flame, Package,
  ClipboardCheck, Banknote, TrendingUp, ShieldAlert, Users2, BadgeCheck, Repeat, Loader2,
  AlarmClock, FileText
} from "lucide-react";

function Kpi({ icon: Icon, label, value, tone = "slate", onClick, testid }) {
  const tones = { slate: "#475569", red: "#DC2626", amber: "#D97706", green: "#16A34A", blue: "#0284C7", purple: "#9333EA" };
  return (
    <button data-testid={testid} onClick={onClick} className="kpi-card text-left bg-white border border-slate-200 rounded-md p-4 w-full">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        <Icon className="h-4 w-4" style={{ color: tones[tone] }} />
      </div>
      <div className="mt-2 text-2xl font-head font-bold" style={{ color: tones[tone] }}>{value}</div>
    </button>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { company } = useTenant();
  const nav = useNavigate();
  const cur = company?.currency || "EUR";
  const [endingOpen, setEndingOpen] = useState(false);
  const [contractsOpen, setContractsOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/company/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const renew = async (t) => {
    setBusy(t.id);
    try { const { data } = await api.post(`/job-templates/${t.id}/extend`, { months: 12 }); toast.success(`${t.ref} extended · ${data.created} new visit(s)`); await load(); }
    catch (e) { toast.error("Extend failed"); } finally { setBusy(null); }
  };

  if (!d) return <div className="text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-head font-bold text-slate-900">Operations Dashboard</h1>
        <p className="text-sm text-slate-500">Live operational view for {company?.name}</p>
      </div>

      <section>
        <h2 className="text-sm font-semibold text-slate-500 mb-2">Operations</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Kpi testid="kpi-jobs-today" icon={Wrench} label="Jobs Today" value={d.jobs_today} tone="blue" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-jobs-week" icon={CalendarClock} label="Jobs This Week" value={d.jobs_week} onClick={() => nav("/app/schedule")} />
          <Kpi testid="kpi-unassigned" icon={UserX} label="Unassigned" value={d.unassigned} tone="amber" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-overdue" icon={AlertTriangle} label="Overdue" value={d.overdue} tone="red" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-emergency" icon={Flame} label="Emergency" value={d.emergency} tone="red" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-materials" icon={Package} label="Awaiting Materials" value={d.awaiting_materials} tone="amber" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-po" icon={ClipboardCheck} label="Awaiting PO" value={d.awaiting_po} tone="amber" />
          <Kpi testid="kpi-approval" icon={ClipboardCheck} label="Awaiting Approval" value={d.awaiting_approval} tone="amber" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-hrdefects" icon={ShieldAlert} label="High-risk Defects" value={d.high_risk_defects} tone="red" onClick={() => nav("/app/defects")} />
          <Kpi testid="kpi-quotes" icon={TrendingUp} label="Open Quotations" value={d.quotes_open} tone="purple" onClick={() => nav("/app/quotations")} />
          <Kpi testid="kpi-pipeline" icon={TrendingUp} label="Pipeline Value" value={money(d.pipeline_value, cur)} tone="purple" onClick={() => nav("/app/quotations")} />
          <Kpi testid="kpi-shortages" icon={Package} label="Stock Shortages" value={d.stock_shortages} tone="amber" onClick={() => nav("/app/stock")} />
          <Kpi testid="kpi-techs" icon={Users2} label="Technicians" value={d.technicians} />
          <Kpi testid="kpi-quals" icon={ShieldAlert} label="Expiring Quals" value={d.expiring_quals} tone="amber" onClick={() => nav("/app/employees")} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-500 mb-2">Billing Readiness</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kpi testid="kpi-ready" icon={Banknote} label="Ready for Invoicing" value={d.ready_to_send} tone="purple" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-value-ready" icon={Banknote} label="Value Ready" value={money(d.value_ready, cur)} tone="purple" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-sent" icon={Banknote} label="Awaiting Approval" value={d.sent_to_accounts} tone="amber" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-sage" icon={Banknote} label="Paid" value={d.awaiting_sage} tone="green" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-queries" icon={AlertTriangle} label="Billing Queries" value={d.billing_queries} tone="red" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-invoiced" icon={BadgeCheck} label="Invoiced" value={d.invoiced_month} tone="blue" onClick={() => nav("/app/billing")} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-500 mb-2">Alerts</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kpi testid="kpi-sla-risk" icon={AlarmClock} label="SLA At Risk (24h)" value={d.sla_at_risk ?? 0} tone="red" onClick={() => nav("/app/jobs")} />
          <Kpi testid="kpi-invoicing-overdue" icon={AlertTriangle} label="Invoicing Overdue (>7d)" value={d.invoicing_overdue} tone="red" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-unpaid-overdue" icon={AlertTriangle} label="Unpaid Invoices (>30d)" value={d.unpaid_overdue} tone="red" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-unpaid-value" icon={Banknote} label="Invoiced (unpaid) Value" value={money(d.unpaid_value, cur)} tone="amber" onClick={() => nav("/app/billing")} />
          <Kpi testid="kpi-templates-ending" icon={CalendarClock} label="Recurring Ending (30d)" value={d.templates_ending} tone="amber" onClick={() => setEndingOpen(true)} />
          <Kpi testid="kpi-contracts-ending" icon={FileText} label="Contracts Ending (60d)" value={d.contracts_ending ?? 0} tone="amber" onClick={() => setContractsOpen(true)} />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-4">
          <h3 className="font-head font-semibold mb-3">Recent Jobs</h3>
          <div className="space-y-1">
            {d.recent_jobs.map((j) => (
              <button key={j.id} onClick={() => nav("/app/jobs")} className="w-full flex items-center justify-between py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 px-1 rounded transition-colors text-left">
                <div><span className="font-medium text-sm text-slate-800">{j.ref}</span> <span className="text-sm text-slate-500">· {j.title}</span>
                  <div className="text-xs text-slate-400">{j.customer_name} · {j.site_name}</div></div>
                <StatusBadge status={j.operational_status} />
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="font-head font-semibold mb-3">Jobs by Status</h3>
          <div className="space-y-2">
            {Object.entries(d.status_breakdown).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <StatusBadge status={k} /><span className="font-medium text-slate-700">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={endingOpen} onOpenChange={setEndingOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><CalendarClock className="h-4 w-4" />Templates Ending Soon</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Extend an agreement in one click to keep its recurring visits flowing.</p>
          <div className="space-y-2 max-h-80 overflow-y-auto scroll-thin">
            {(d.templates_ending_list || []).map((t) => (
              <div key={t.id} data-testid={`ending-row-${t.id}`} className="flex items-center justify-between border border-slate-200 rounded-md p-2.5">
                <div><div className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Repeat className="h-3.5 w-3.5 accent-text" />{t.title}</div>
                  <div className="text-xs text-slate-400">{t.ref} · {t.frequency} · ends {fmtDate(t.end_date)}</div></div>
                <Button size="sm" className="btn-accent" data-testid={`ending-renew-${t.id}`} onClick={() => renew(t)} disabled={busy === t.id}>
                  {busy === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Renew +12m"}</Button>
              </div>
            ))}
            {(!d.templates_ending_list || d.templates_ending_list.length === 0) && <p className="text-sm text-slate-400 text-center py-6">No templates ending in the next 30 days.</p>}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={contractsOpen} onOpenChange={setContractsOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><FileText className="h-4 w-4" />Contracts Ending / Renewing Soon</DialogTitle>
            <DialogDescription>Contracts with an end or renewal date in the next 60 days — renew before they lapse.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto scroll-thin">
            {(d.contracts_ending_list || []).map((c) => (
              <div key={c.id} data-testid={`contract-ending-${c.id}`} className="flex items-center justify-between border border-slate-200 rounded-md p-2.5">
                <div><div className="text-sm font-medium text-slate-800">{c.ref} · {c.name}</div>
                  <div className="text-xs text-slate-400">{c.site_name || "—"} · ends {fmtDate(c.end_date)}</div></div>
                <Button size="sm" variant="outline" data-testid={`contract-ending-open-${c.id}`} onClick={() => { setContractsOpen(false); nav("/app/contracts"); }}>Open</Button>
              </div>
            ))}
            {(!d.contracts_ending_list || d.contracts_ending_list.length === 0) && <p className="text-sm text-slate-400 text-center py-6">No contracts ending in the next 60 days.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
